import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  collection, addDoc, updateDoc, doc, getDoc, query,
  where, getDocs, setDoc, deleteDoc,
} from "firebase/firestore";
import {
  Box, Typography, TextField, Grid, Button, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Chip, Divider, IconButton, Paper, InputAdornment,
  Alert, CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Tooltip, Badge, Autocomplete
} from "@mui/material";
import {
  Add, Remove, ExpandMore, LocalOffer, WarningAmber,
  Assessment, Inventory2, Save, Clear, Category,
  TrendingUp, AttachMoney, Close
} from "@mui/icons-material";
import { db } from "../firebase-config";
import InventoryIcon from '@mui/icons-material/Inventory2';      // For Product
import RestaurantIcon from '@mui/icons-material/Restaurant';      // For Ingredient
import BuildCircleIcon from '@mui/icons-material/BuildCircle';

const InventoryForm = ({ editItem, onSubmitSuccess = () => { }, onClose }) => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ingredientError, setIngredientError] = useState({});
  const [categories, setCategories] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const unitOptions = [
    "kg", "g", "gram", "grams", "litre", "l", "ml", "milliliter", "milliliters",
    "pack", "packs", "item", "items", "box", "boxes", "dozen", "bottle", "bottles",
    "piece", "pieces", "pcs"
  ];

  // **CRISPY DOSA THEME**
  const crispyTheme = {
    colors: {
      primary: "#2E7D32",
      secondary: "#FF6B35",
      accent: "#FFC107",
      success: "#4CAF50",
      warning: "#FF9800",
      error: "#F44336",
      surface: "#FAFAFA",
      background: "#FFFFFF",
      text: {
        primary: "#212121",
        secondary: "#757575",
      }
    },
    gradients: {
      primary: "linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)",
      secondary: "linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)",
      accent: "linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)",
    }
  };

  const {
    register, handleSubmit, control, watch, setValue,
    formState: { errors }, reset,
  } = useForm({
    defaultValues: {
      id: "",
      title: "",
      brand: "",
      vendor: "",
      categoryId: "",
      units: "",
      availableQuantity: "0",
      soldQuantity: "0",
      actualPrice: "0",
      sellingPrice: "0", // This will always be the BASE price (without VAT)
      itemType: "ingredient",
      showInMobile: true,
      ingredients: [],
      // **NEW FIELDS**
      hasVAT: false,
      lowStockThreshold: 5,
      orderingThreshold: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ingredients",
  });

  const watchItemType = watch("itemType");
  const watchIngredients = watch("ingredients");
  const watchAvailableQuantity = watch("availableQuantity");
  const watchSellingPrice = watch("sellingPrice");
  const watchHasVAT = watch("hasVAT");

  // **SAFE NUMBER HELPERS**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  // **VAT CALCULATION FUNCTIONS**
  const calculateVATAmount = (basePrice, hasVAT) => {
    return hasVAT ? (safeNumber(basePrice) * 0.2) : 0;
  };

  const calculatePriceWithVAT = (basePrice, hasVAT) => {
    return hasVAT ? (safeNumber(basePrice) * 1.2) : safeNumber(basePrice);
  };

  // **FETCH DATA**
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        setLoading(true);
        const ingredientsQuery = query(collection(db, "ingredients"));
        const ingredientsSnapshot = await getDocs(ingredientsQuery);
        const ingredientsList = ingredientsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setIngredients(ingredientsList);
      } catch (err) {
        console.error("Error fetching ingredients:", err);
        setError("Failed to load ingredients");
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const categoriesQuery = query(collection(db, "inventoryCategory"));
        const categoriesSnapshot = await getDocs(categoriesQuery);
        const categoriesList = categoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoriesList);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    fetchIngredients();
    fetchCategories();
  }, []);

  // **SET EDIT ITEM DATA**
  useEffect(() => {
    if (editItem) {
      // **IMPORTANT: Store the base selling price (without VAT) in the form**
      const baseSellingPrice = editItem.hasVAT
        ? (safeNumber(editItem.sellingPrice)) // Convert back to base price if VAT was applied
        : safeNumber(editItem.sellingPrice);

      reset({
        ...editItem,
        availableQuantity: editItem.availableQuantity?.toString() || "0",
        soldQuantity: editItem.soldQuantity?.toString() || "0",
        actualPrice: editItem.actualPrice?.toString() || "0",
        sellingPrice: safeToFixed(baseSellingPrice), // Always store base price
        // ✅ FIXED: Proper boolean conversion for toggles
        hasVAT: Boolean(editItem.hasVAT),
        showInMobile: editItem.showInMobile !== undefined ? Boolean(editItem.showInMobile) : true,
        lowStockThreshold: editItem.lowStockThreshold || 5,
        orderingThreshold: editItem.orderingThreshold || 0,
      });
    }
  }, [editItem, reset]);

  // **INGREDIENT PRICE UPDATES**
  const updateIngredientPrices = (index, ingredientId) => {
    const selectedIngredient = ingredients.find(ing => ing.id === ingredientId);
    if (!selectedIngredient) return;

    const quantityPerUnit = safeNumber(watchIngredients[index]?.quantityPerUnit);
    const actualPrice = safeNumber(selectedIngredient.actualPrice) * quantityPerUnit;

    // **ALWAYS STORE BASE SELLING PRICE FOR INGREDIENTS**
    const baseSellingPrice = selectedIngredient.hasVAT
      ? (safeNumber(selectedIngredient.sellingPrice) / 1.2)
      : safeNumber(selectedIngredient.sellingPrice);

    const ingredientSellingPrice = baseSellingPrice * quantityPerUnit;

    setValue(`ingredients.${index}.units`, selectedIngredient.units);
    setValue(`ingredients.${index}.actualPrice`, safeToFixed(actualPrice));
    setValue(`ingredients.${index}.sellingPrice`, safeToFixed(ingredientSellingPrice));
  };

  // **CALCULATE DERIVED PRODUCT PRICES**
  useEffect(() => {
    if (watchItemType === "derived" && watchIngredients?.length > 0) {
      let totalActualPrice = 0;
      let totalSellingPrice = 0;

      watchIngredients.forEach((ingredient) => {
        totalActualPrice += safeNumber(ingredient.actualPrice);
        totalSellingPrice += safeNumber(ingredient.sellingPrice);
      });

      // **STORE BASE PRICES - VAT WILL BE CALCULATED ON DISPLAY/SAVE**
      setValue("actualPrice", safeToFixed(totalActualPrice));
      setValue("sellingPrice", safeToFixed(totalSellingPrice));
    }
  }, [watchIngredients, watchItemType, setValue]);

  // **FORM SUBMISSION - FIXED VAT LOGIC**
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      // Validate derived item ingredients
      if (data.itemType === "derived") {
        if (!data.ingredients || data.ingredients.length === 0) {
          setError("Derived products must have at least one ingredient");
          setLoading(false);
          return;
        }

        let totalActualPrice = 0;
        let totalSellingPrice = 0;

        data.ingredients.forEach((ingredient) => {
          totalActualPrice += safeNumber(ingredient.actualPrice);
          totalSellingPrice += safeNumber(ingredient.sellingPrice);
        });

        data.actualPrice = totalActualPrice;
        data.sellingPrice = totalSellingPrice; // Base price
      }

      // **CRITICAL FIX: Convert all number fields but keep sellingPrice as base price**
      data.availableQuantity = safeNumber(data.availableQuantity);
      data.soldQuantity = safeNumber(data.soldQuantity);
      data.actualPrice = safeNumber(data.actualPrice);
      data.sellingPrice = safeNumber(data.sellingPrice); // Keep as base price
      data.lowStockThreshold = safeNumber(data.lowStockThreshold);
      data.orderingThreshold = safeNumber(data.orderingThreshold);

      // **Store hasVAT flag but don't modify the sellingPrice here**
      // The display logic will handle VAT calculation when showing prices

      // Add timestamps
      data.updatedAt = new Date().toISOString();

      let result;
      const collectionName = data.itemType === "ingredient" ? "ingredients" : "inventoryItems";

      if (!editItem) {
        // CREATE NEW ITEM
        data.createdAt = new Date().toISOString();
        data.soldQuantity = 0;

        const newDocRef = doc(collection(db, collectionName));
        data.id = newDocRef.id;
        await setDoc(newDocRef, data);
        result = { ...data };
      } else {
        // UPDATE EXISTING ITEM
        if (!editItem.id) {
          throw new Error("Invalid document ID");
        }

        const docRef = doc(db, collectionName, editItem.id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          const altCollection = data.itemType === "ingredient" ? "inventoryItems" : "ingredients";
          const altDocRef = doc(db, altCollection, editItem.id);
          const altDocSnap = await getDoc(altDocRef);

          if (altDocSnap.exists()) {
            await deleteDoc(altDocRef);
            const newDocRef = doc(db, collectionName, editItem.id);
            await setDoc(newDocRef, {
              ...data,
              id: editItem.id,
              createdAt: editItem.createdAt || new Date().toISOString(),
            });
          } else {
            throw new Error("Document not found in either collection");
          }
        } else {
          await updateDoc(docRef, data);
        }

        result = { ...data, id: editItem.id };
      }

      onSubmitSuccess(result);
      setSnackbarMessage(editItem ? "Item updated successfully!" : "Item created successfully!");
      setSnackbarOpen(true);
      if (onClose) onClose();
    } catch (err) {
      console.error("Error saving item:", err);
      setError("Failed to save item: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = () => {
    append({
      id: Date.now(),
      ingredientId: "",
      title: "",
      quantityPerUnit: "0",
      totalQuantityUsed: "0",
      units: "",
      actualPrice: "0",
      sellingPrice: "0",
    });
  };

  return (
    <Box sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      position: "relative"
    }}>
      {/* **CLOSE BUTTON INSIDE** */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          zIndex: 1000,
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          color: 'black',
          "&:hover": {
            backgroundColor: "rgba(25, 5, 3, 0.2)",
            transform: "scale(1.1)",
          },
        }}
      >
        <Close />
      </IconButton>

      {/* **SCROLLABLE CONTENT** */}
      <Box sx={{
        flex: 1,
        overflowY: "auto",
        padding: "0 24px 24px 24px",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: crispyTheme.colors.primary,
          borderRadius: "4px",
          "&:hover": {
            background: crispyTheme.colors.success,
          },
        },
      }}>
        {/* **HEADER** */}
        <Box sx={{
          background: crispyTheme.gradients.primary,
          color: "#FFFFFF",
          padding: "32px 24px 24px 24px",
          borderRadius: "0 0 16px 16px",
          marginBottom: "24px",
          marginTop: 0,
          marginLeft: "-24px",
          marginRight: "-24px",
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, marginBottom: "8px", marginTop: "8px" }}>
            {editItem ? "✏️ Edit Inventory Item" : "➕ Add New Inventory Item"}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {editItem ? "Update product information and settings" : "Create a new product for your inventory"}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "24px", borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* **BASIC INFORMATION SECTION** */}
          <Card sx={{ marginBottom: "24px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(46, 125, 50, 0.1)" }}>
            <CardContent sx={{ padding: "24px" }}>
              <Typography variant="h6" sx={{
                color: crispyTheme.colors.primary,
                fontWeight: 700,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                <Inventory2 /> Basic Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Item Type</InputLabel>
                    <Controller
                      name="itemType"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Item Type">
                          <MenuItem value="ingredient"><InventoryIcon /> Ingredient</MenuItem>
                          <MenuItem value="product"><RestaurantIcon /> Product</MenuItem>
                          <MenuItem value="derived"><BuildCircleIcon /> Derived Product</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product Title"
                    {...register("title", { required: "Title is required" })}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Category sx={{ color: crispyTheme.colors.primary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Brand"
                    {...register("brand", { required: "Brand is required" })}
                    error={!!errors.brand}
                    helperText={errors.brand?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Vendor"
                    {...register("vendor", { required: "Vendor is required" })}
                    error={!!errors.vendor}
                    helperText={errors.vendor?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.categoryId}>
                    <InputLabel>Category</InputLabel>
                    <Controller
                      name="categoryId"
                      control={control}
                      rules={{ required: "Category is required" }}
                      render={({ field }) => (
                        <Select {...field} label="Category">
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.category}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.categoryId && (
                      <Typography variant="caption" color="error">
                        {errors.categoryId.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Units"
                    {...register("units", { required: "Units is required" })}
                    error={!!errors.units}
                    helperText={errors.units?.message || "e.g. Pack, Box, Kg, Litre"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ marginBottom: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(255, 107, 53, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#FF6B35', fontWeight: 700, marginBottom: 2 }}>
                Order Calculation Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="oneUnitQuantity"
                    control={control}
                    defaultValue={0}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        label="Amount in One Unit"
                        helperText="How much quantity does one unit represent (e.g., 2.5)"
                        inputProps={{ min: 0, step: "any" }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                <Controller
                  name="oneUnitQuantityUnit"
                  control={control}
                  defaultValue={null}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      freeSolo
                      options={unitOptions}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Quantity Unit"
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                />

              </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* **QUANTITY & PRICING SECTION** */}
          <Card sx={{ marginBottom: "24px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(46, 125, 50, 0.1)" }}>
            <CardContent sx={{ padding: "24px" }}>
              <Typography variant="h6" sx={{
                color: crispyTheme.colors.secondary,
                fontWeight: 700,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                Quantity & Pricing
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Available Quantity"
                    type="number"
                    inputProps={{ step: "any", min: 0 }}
                    {...register("availableQuantity", {
                      required: "Available quantity is required",
                      min: { value: 0, message: "Quantity cannot be negative" },
                    })}
                    error={!!errors.availableQuantity}
                    helperText={errors.availableQuantity?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sold Quantity"
                    type="number"
                    inputProps={{ step: "any", min: 0 }}
                    {...register("soldQuantity")}
                    disabled
                    helperText="This is calculated automatically"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Actual Price per Unit"
                    type="number"
                    inputProps={{ step: "any", min: 0 }}
                    {...register("actualPrice", {
                      required: "Actual price is required",
                      min: { value: 0, message: "Price cannot be negative" },
                    })}
                    error={!!errors.actualPrice}
                    helperText={errors.actualPrice?.message}
                    disabled={watchItemType === "derived"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Base Selling Price per Unit"
                    type="number"
                    inputProps={{ step: "any", min: 0 }}
                    {...register("sellingPrice", {
                      required: "Selling price is required",
                      min: { value: 0, message: "Price cannot be negative" },
                    })}
                    error={!!errors.sellingPrice}
                    helperText="Enter price WITHOUT VAT - VAT will be calculated automatically if enabled"
                    disabled={watchItemType === "derived"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">£</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>


          {/* **NEW ENHANCEMENTS SECTION** */}
          <Card sx={{ marginBottom: "24px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(255, 107, 53, 0.1)" }}>
            <CardContent sx={{ padding: "24px" }}>
              <Typography variant="h6" sx={{
                color: crispyTheme.colors.accent,
                fontWeight: 700,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                <TrendingUp /> Advanced Settings
              </Typography>

              <Grid container spacing={3}>
                {/* ✅ FIXED: VAT TOGGLE with Controller */}
                <Grid item xs={12}>
                  <Controller
                    name="hasVAT"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: crispyTheme.colors.accent,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: crispyTheme.colors.accent,
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalOffer sx={{ color: crispyTheme.colors.accent }} />
                            <Typography sx={{ fontWeight: 600 }}>Apply VAT (20%)</Typography>
                            {watchHasVAT && (
                              <Chip
                                label="VAT ENABLED"
                                size="small"
                                sx={{
                                  backgroundColor: crispyTheme.colors.accent,
                                  color: "#FFFFFF",
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    )}
                  />
                </Grid>



                {/* **VAT PRICE DISPLAY - FIXED CALCULATION** */}
                {watchHasVAT && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ borderRadius: "12px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                        <Typography variant="body2">
                          <strong>Base Price (Without VAT):</strong>
                        </Typography>
                        <Typography variant="h6" sx={{ color: crispyTheme.colors.primary, fontWeight: 700 }}>
                          £{safeToFixed(watchSellingPrice)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                        <Typography variant="body2">
                          <strong>VAT Amount (20%):</strong>
                        </Typography>
                        <Typography variant="h6" sx={{ color: crispyTheme.colors.accent, fontWeight: 700 }}>
                          £{safeToFixed(calculateVATAmount(watchSellingPrice, true))}
                        </Typography>
                      </Box>
                      <Divider sx={{ margin: "8px 0" }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1">
                          <strong>Final Selling Price (Including VAT):</strong>
                        </Typography>
                        <Typography variant="h5" sx={{ color: crispyTheme.colors.secondary, fontWeight: 700 }}>
                          £{safeToFixed(calculatePriceWithVAT(watchSellingPrice, true))}
                        </Typography>
                      </Box>
                    </Alert>
                  </Grid>
                )}

                {/* **2. LOW STOCK THRESHOLD** */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Low Stock Threshold"
                    type="number"
                    inputProps={{ min: 1 }}
                    {...register("lowStockThreshold", {
                      required: "Low stock threshold is required",
                      min: { value: 1, message: "Threshold must be at least 1" },
                    })}
                    error={!!errors.lowStockThreshold}
                    helperText={errors.lowStockThreshold?.message || "Alert when stock falls below this number"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WarningAmber sx={{ color: crispyTheme.colors.warning }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* **3. ORDERING THRESHOLD** */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Monthly Ordering Threshold"
                    type="number"
                    inputProps={{ min: 0 }}
                    {...register("orderingThreshold", {
                      min: { value: 0, message: "Threshold cannot be negative" },
                    })}
                    error={!!errors.orderingThreshold}
                    helperText={errors.orderingThreshold?.message || "Minimum orders expected per month for analytics"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Assessment sx={{ color: crispyTheme.colors.primary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* ✅ FIXED: MOBILE APP TOGGLE with Controller */}
                <Grid item xs={12}>
                  <Controller
                    name="showInMobile"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600 }}>Show in Mobile App</Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </Grid>

              </Grid>
            </CardContent>
          </Card>

          {/* **INGREDIENTS SECTION (FOR DERIVED PRODUCTS)** */}
          {watchItemType === "derived" && (
            <Card sx={{ marginBottom: "24px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(76, 175, 80, 0.1)" }}>
              <CardContent sx={{ padding: "24px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <Typography variant="h6" sx={{
                    color: crispyTheme.colors.success,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                    🧩 Ingredients Configuration
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddIngredient}
                    startIcon={<Add />}
                    sx={{
                      backgroundColor: crispyTheme.colors.success,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600
                    }}
                  >
                    Add Ingredient
                  </Button>
                </Box>

                {fields.length === 0 && (
                  <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                    At least one ingredient is required for derived products
                  </Alert>
                )}

                {fields.map((field, index) => (
                  <Accordion key={field.id} sx={{ marginBottom: "16px", borderRadius: "12px" }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                        <Badge badgeContent={index + 1} color="primary">
                          <Inventory2 />
                        </Badge>
                        <Typography sx={{ fontWeight: 600 }}>
                          {watchIngredients[index]?.title || `Ingredient #${index + 1}`}
                        </Typography>
                        <Box sx={{ marginLeft: "auto" }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              remove(index);
                            }}
                            color="error"
                            size="small"
                          >
                            <Remove />
                          </IconButton>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Select Ingredient</InputLabel>
                            <Controller
                              name={`ingredients.${index}.ingredientId`}
                              control={control}
                              rules={{ required: "Ingredient is required" }}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Select Ingredient"
                                  onChange={(e) => {
                                    field.onChange(e);
                                    const selectedIngredient = ingredients.find(ing => ing.id === e.target.value);
                                    if (selectedIngredient) {
                                      setValue(`ingredients.${index}.title`, selectedIngredient.title);
                                    }
                                    updateIngredientPrices(index, e.target.value);
                                  }}
                                >
                                  {ingredients.map((ing) => (
                                    <MenuItem key={ing.id} value={ing.id}>
                                      {ing.title} ({safeToFixed(ing.availableQuantity, 0)} {ing.units} available)
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                            {errors.ingredients?.[index]?.ingredientId && (
                              <Typography variant="caption" color="error">
                                {errors.ingredients[index].ingredientId.message}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Quantity per Unit"
                            type="number"
                            inputProps={{ step: "any", min: 0.01 }}
                            {...register(`ingredients.${index}.quantityPerUnit`, {
                              required: "Quantity is required",
                              min: { value: 0.01, message: "Quantity must be positive" },
                            })}
                            error={!!errors.ingredients?.[index]?.quantityPerUnit}
                            helperText={errors.ingredients?.[index]?.quantityPerUnit?.message || "How much of this ingredient per unit"}
                            onChange={(e) => {
                              register(`ingredients.${index}.quantityPerUnit`).onChange(e);
                              updateIngredientPrices(index, watchIngredients[index]?.ingredientId);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Units"
                            {...register(`ingredients.${index}.units`)}
                            disabled
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Actual Price"
                            {...register(`ingredients.${index}.actualPrice`)}
                            disabled
                            InputProps={{
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Base Selling Price"
                            {...register(`ingredients.${index}.sellingPrice`)}
                            disabled
                            InputProps={{
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Total Used"
                            value={safeToFixed(
                              safeNumber(watchIngredients[index]?.quantityPerUnit) *
                              safeNumber(watchAvailableQuantity)
                            )}
                            disabled
                            helperText="Auto-calculated"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          )}

          {/* **ACTION BUTTONS** */}
          <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            padding: "24px",
            backgroundColor: crispyTheme.colors.surface,
            borderRadius: "16px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            marginLeft: "-24px",
            marginRight: "-24px",
            marginBottom: "-24px",
          }}>
            <Button
              variant="outlined"
              onClick={() => reset()}
              startIcon={<Clear />}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: crispyTheme.colors.text.secondary,
                color: crispyTheme.colors.text.secondary
              }}
            >
              Reset Form
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              sx={{
                background: crispyTheme.gradients.primary,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "140px"
              }}
            >
              {loading ? "Saving..." : editItem ? "Update Item" : "Create Item"}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default InventoryForm;
